<template>
    <div class="users-filters">
        <div class="users-filters__status" role="radiogroup" aria-label="Статус">
            <button
                v-for="option in USER_STATUS_OPTIONS"
                :key="option.value"
                type="button"
                role="radio"
                :aria-checked="status === option.value"
                :class="['status-segment', { 'is-active': status === option.value }]"
                @click="status = option.value"
            >
                {{ option.label }}
                <span class="status-segment__count">{{ statusCounts[option.value] ?? 0 }}</span>
            </button>
        </div>

        <div class="users-filters__fields">
            <label class="users-filters__field users-filters__field--search">
                <span class="visually-hidden">Поиск по email</span>
                <input v-model.trim="searchQuery" type="search" class="form-input" placeholder="Поиск по email" />
            </label>
            <label class="users-filters__field">
                <span class="users-filters__label">Роль</span>
                <select v-model="roleCode" class="select-input">
                    <option value="">Все</option>
                    <option v-for="option in roleOptions" :key="option.code" :value="option.code">
                        {{ option.label }}
                    </option>
                </select>
            </label>
            <label v-if="partners.length > 1" class="users-filters__field">
                <span class="users-filters__label">Партнёр</span>
                <select v-model="partnerId" class="select-input">
                    <option value="">Все</option>
                    <option v-for="partner in partners" :key="partner.id" :value="String(partner.id)">
                        {{ partner.name }}
                    </option>
                </select>
            </label>
            <button v-if="hasActiveFilters" type="button" class="users-filters__reset" @click="reset">
                Сбросить
            </button>
        </div>
    </div>
</template>

<script setup>
import { computed } from 'vue';
import { ROLE, ROLE_FILTER_OPTIONS } from '../../../constants/roles';
import { USER_STATUS_OPTIONS } from '../../../constants/users';

const props = defineProps({
    partners: { type: Array, required: true },
    statusCounts: { type: Object, required: true },
    showAdminRole: { type: Boolean, default: false },
});

const status = defineModel('status', { type: String, required: true });
const roleCode = defineModel('roleCode', { type: String, default: '' });
const partnerId = defineModel('partnerId', { type: String, default: '' });
const searchQuery = defineModel('searchQuery', { type: String, default: '' });

const roleOptions = computed(() => (props.showAdminRole
    ? ROLE_FILTER_OPTIONS
    : ROLE_FILTER_OPTIONS.filter(option => option.code !== ROLE.ADMIN)));

const hasActiveFilters = computed(() => Boolean(roleCode.value || partnerId.value || searchQuery.value));

const reset = () => {
    roleCode.value = '';
    partnerId.value = '';
    searchQuery.value = '';
};
</script>

<style scoped lang="scss">
@use '../../../../scss/pages/admin/admin-tokens' as *;

.users-filters {
    display: flex;
    flex-direction: column;
    gap: $spacing-md;
    flex: 1 1 auto;
    min-width: 0;
}

.users-filters__status {
    display: inline-flex;
    align-self: flex-start;
    padding: 3px;
    border-radius: $radius-lg;
    background: $admin-bg-hover;
}

.status-segment {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    padding: 0.35rem 0.9rem;
    border: none;
    border-radius: $radius-md;
    background: transparent;
    font-size: $font-size-sm;
    font-weight: 500;
    color: $admin-text-muted;
    cursor: pointer;

    &.is-active {
        background: $admin-bg-primary;
        color: $admin-text-primary;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.08);
    }
}

.status-segment__count {
    min-width: 1.25rem;
    padding: 0 0.3rem;
    border-radius: 999px;
    background: rgba(0, 0, 0, 0.06);
    font-size: $font-size-xs;
    text-align: center;
}

.users-filters__fields {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: $spacing-md;
}

.users-filters__field {
    display: inline-flex;
    align-items: center;
    gap: $spacing-sm;
    margin: 0;

    &--search {
        flex: 1 1 240px;
        max-width: 360px;

        .form-input { padding: 0.4rem $spacing-md; }
    }
}

.users-filters__label {
    font-size: $font-size-sm;
    color: $admin-text-muted;
}

.users-filters__reset {
    border: none;
    background: none;
    padding: 0;
    font-size: $font-size-sm;
    color: $admin-primary-color;
    cursor: pointer;

    &:hover { text-decoration: underline; }
}

.visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
    white-space: nowrap;
}
</style>
